import React, { useState, useRef, useEffect } from "react";
import { API_BASE } from "../../api/config";

const API = `${API_BASE}/ki`;

// Markdown-ähnliche Formatierung für KI-Antworten
function FormatierterText({ text }) {
  if (!text) return null;
  // Einfache Formatierung: **fett**, `code`, Zeilenumbrüche
  const teile = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <span>
      {teile.map((t, i) => {
        if (t.startsWith("**") && t.endsWith("**"))
          return <strong key={i}>{t.slice(2, -2)}</strong>;
        if (t.startsWith("`") && t.endsWith("`"))
          return <code key={i}>{t.slice(1, -1)}</code>;
        return t.split("\n").map((line, j) => (
          <React.Fragment key={`${i}-${j}`}>
            {j > 0 && <br />}
            {line}
          </React.Fragment>
        ));
      })}
    </span>
  );
}

export default function ChatFenster({
  sessionId,           // bestehende Session (Lern oder Chat)
  mode = "lern",       // "lern" | "chat"
  initialMessage,      // erste KI-Nachricht aus Session-Start
  onDone,              // Callback wenn Session abgeschlossen
  onLearnedRule,       // Callback wenn Regel gelernt wurde
}) {
  const [messages, setMessages] = useState(
    initialMessage ? [{ role: "assistant", content: initialMessage }] : []
  );
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState("analyse");
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendeNachricht = async (nachricht) => {
    if (!nachricht.trim() || sending) return;
    const endpoint = mode === "lern"
      ? `${API}/lern/session/${sessionId}/message`
      : `${API}/chat/${sessionId}/message`;

    // User-Nachricht sofort anzeigen
    setMessages((prev) => [...prev, { role: "user", content: nachricht }]);
    setInput("");
    setSending(true);

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: nachricht }),
      });
      const data = await res.json();

      setMessages((prev) => [...prev, { role: "assistant", content: data.message }]);

      if (data.step) setStep(data.step);
      if (data.done) {
        setDone(true);
        onDone?.();
      }
      if (data.learned_rule) onLearnedRule?.(data.learned_rule);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Fehler: ${e.message}`, error: true },
      ]);
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendeNachricht(input);
    }
  };

  // Schnellantworten je nach Step
  const schnellAntworten = () => {
    if (step === "analyse")
      return ["Genau, das war der Fehler!", "Ich sehe das anders...", "Was hättest du stattdessen prüfen sollen?"];
    if (step === "regel")
      return ["Speichern ✅", "Regel anpassen...", "Nochmal erklären"];
    return [];
  };

  return (
    <div className={`chat-fenster ${done ? "done" : ""}`}>
      <div className="chat-header">
        <span className="chat-titel">
          {mode === "lern" ? "🤖 KI-Lernbot" : "💬 imho Chatbot"}
        </span>
        {step && mode === "lern" && (
          <span className={`chat-step step-${step}`}>
            {step === "analyse" && "🔍 Analyse"}
            {step === "regel" && "📝 Regelvorschlag"}
            {step === "done" && "✅ Abgeschlossen"}
          </span>
        )}
      </div>

      <div className="chat-verlauf">
        {messages.length === 0 && (
          <div className="chat-leer">
            Noch keine Nachrichten. Die KI antwortet gleich...
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`chat-nachricht ${msg.role} ${msg.error ? "error" : ""}`}>
            <span className="nachricht-icon">
              {msg.role === "assistant" ? "🤖" : "👤"}
            </span>
            <div className="nachricht-text">
              <FormatierterText text={msg.content} />
            </div>
          </div>
        ))}
        {sending && (
          <div className="chat-nachricht assistant typing">
            <span className="nachricht-icon">🤖</span>
            <div className="nachricht-text">
              <span className="typing-dots">
                <span>.</span><span>.</span><span>.</span>
              </span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {!done && (
        <>
          {schnellAntworten().length > 0 && (
            <div className="schnell-antworten">
              {schnellAntworten().map((antwort) => (
                <button
                  key={antwort}
                  className="schnell-btn"
                  onClick={() => sendeNachricht(antwort)}
                  disabled={sending}
                >
                  {antwort}
                </button>
              ))}
            </div>
          )}
          <div className="chat-input-bereich">
            <textarea
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nachricht schreiben... (Enter = Senden)"
              rows={2}
              disabled={sending}
            />
            <button
              className="sende-btn"
              onClick={() => sendeNachricht(input)}
              disabled={sending || !input.trim()}
            >
              {sending ? "⏳" : "➤"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
